<template>
  <div class="entity-navigator" v-if="hasAncestry">
    <!-- Single Path Display (most common case) -->
    <div v-if="paths.length === 1" class="flex items-center gap-2 text-sm">
      <div class="flex items-center gap-1">
        <span v-if="!hideLabel">Parent:</span>
        <template v-for="(ancestor, index) in paths[0]" :key="ancestor.id">
          <router-link
            :to="`/entity/${ancestor.id}`"
            :class="[canAccessEntity(ancestor.id) ? 'hover:bg-gray-100 group-hover:text-gray-700 group-hover:text-gray-500' : 'pointer-events-none opacity-70']"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-200 group"
          >
            <span class="sensitive text-gray-500">{{ ancestor.name }}</span>
            <span class="text-xs text-gray-400">({{ ancestor.type }})</span>
          </router-link>
          <svg v-if="index < paths[0].length - 1"
               class="w-4 h-4 text-gray-400 shrink-0"
               fill="none"
               stroke="currentColor"
               viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </template>
      </div>
      <Form_Manage_Entity_Parents v-if="editable" :entity-id="entityId" @updated="fetchAncestryPaths" />
    </div>

    <!-- Multiple Paths Display -->
    <div v-else-if="paths.length > 1" class="relative">
      <!-- Compact View (Default) -->
      <div v-if="!showAllPaths" class="flex items-center gap-2">
        <div class="flex items-center gap-1 text-sm">
          <span v-if="!hideLabel">Multiple Parents:</span>
          <div class="flex items-center">
            <template v-for="(ancestor, index) in paths[0]" :key="ancestor.id">
              <router-link
                :to="`/entity/${ancestor.id}`"
                :class="[canAccessEntity(ancestor.id) ? 'hover:bg-gray-100 group-hover:text-gray-700 group-hover:text-gray-500' : 'pointer-events-none opacity-70']"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-200 group"
              >
                <span class="sensitive text-gray-500">{{ ancestor.name }}</span>
                <span class="text-xs text-gray-400">({{ ancestor.type }})</span>
              </router-link>
              <svg v-if="index < paths[0].length - 1"
                   class="w-4 h-4 text-gray-400 shrink-0"
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </template>
          </div>
        </div>

        <!-- Indicator for multiple paths -->
        <button
          @click="showAllPaths = true"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          {{ paths.length - 1 }} more {{ paths.length - 1 === 1 ? 'path' : 'paths' }}
        </button>
      </div>

      <!-- Expanded View -->
      <div v-else class="space-y-2">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-xs font-medium text-gray-500 uppercase tracking-wider">
            {{ paths.length }} Ancestry Paths
          </span>
          <button
            @click="showAllPaths = false"
            class="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            Collapse
          </button>
        </div>

        <Form_Manage_Entity_Parents v-if="editable" :entity-id="entityId" @updated="fetchAncestryPaths" />

        <div class="space-y-1">
          <div v-for="(path, pathIndex) in paths"
               :key="`path-${pathIndex}`"
               class="flex items-center gap-1 text-sm pl-2 border-l-2 border-gray-200 hover:border-blue-400 transition-colors duration-200">
            <div class="flex items-center">
              <template v-for="(ancestor, index) in path" :key="`${pathIndex}-${ancestor.id}`">
                <router-link
                  :to="`/entity/${ancestor.id}`"
                  :class="[canAccessEntity(ancestor.id) ? 'hover:bg-gray-100 group-hover:text-gray-700 group-hover:text-gray-500' : 'pointer-events-none opacity-70']"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-200 group"
                >
                  <span class="sensitive text-gray-500">{{ ancestor.name }}</span>
                  <span class="text-xs text-gray-400">({{ ancestor.type }})</span>
                </router-link>
                <svg v-if="index < path.length - 1"
                     class="w-4 h-4 text-gray-400 shrink-0"
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="flex items-center gap-2 text-sm text-gray-400">
      <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Loading ancestry...</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { canAccessEntity } from '@/utils/access';
import Form_Manage_Entity_Parents from '@/components/Entity/Form_Manage_Entity_Parents.vue';

export default {
  name: 'EntityParentNavigator',
  components: {
    Form_Manage_Entity_Parents
  },
  props: {
    entityId: {
      type: [String, Number],
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['count'],
  data() {
    return {
      paths: [],
      loading: false,
      showAllPaths: false,
      error: null
    };
  },
  computed: {
    hasAncestry() {
      return this.paths.length > 0 || this.loading;
    }
  },
  watch: {
    entityId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchAncestryPaths();
        }
      }
    }
  },
  methods: {
    canAccessEntity,
    async fetchAncestryPaths() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(`/api/entities/${this.entityId}/ancestry_paths/`);
        this.paths = response.data.paths || [];

        // Sort paths by length (shortest first)
        this.paths.sort((a, b) => a.length - b.length);

        // Reset expanded state when new data loads
        this.showAllPaths = false;
      } catch (error) {
        console.error('Error fetching ancestry paths:', error);
        this.error = error.message;
        this.paths = [];
      } finally {
        this.loading = false;
        this.$emit('count', this.paths.length);
      }
    }
  }
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.entity-navigator {
  @apply mb-2;
}

/* Smooth transitions for interactive elements */
.entity-navigator a,
.entity-navigator button {
  @apply transition-all duration-200;
}

/* Subtle animation for path indicators */
@keyframes pulse-subtle {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.entity-navigator button:hover svg {
  animation: pulse-subtle 1s infinite;
}
</style>
